import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Users, Loader2, Calendar, Coins, TrendingUp, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import api, { formatXOF } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function TontineCard({ t }) {
  const pct = t.total_target ? Math.min(100, Math.round((t.total_collected / t.total_target) * 100)) : 0;
  const statusMap = {
    draft: { label: "Brouillon", color: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300" },
    active: { label: "Active", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
    paused: { label: "Pause", color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
    completed: { label: "Terminée", color: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
  };
  const st = statusMap[t.status] || statusMap.draft;

  return (
    <Link to={`/tontines/${t.id}`} className="card-soft p-5 block group" data-testid={`tontine-card-${t.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <code className="text-xs font-mono text-muted-foreground">{t.id}</code>
            {t.role === "organizer" && <Crown className="h-3 w-3 text-amber-500" />}
          </div>
          <h3 className="font-display font-semibold text-lg truncate group-hover:gradient-text transition">{t.name}</h3>
        </div>
        <Badge className={`${st.color} border-0 shrink-0`}>{st.label}</Badge>
      </div>
      {t.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{t.description}</p>}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between text-sm">
          <span className="font-semibold gradient-text">{formatXOF(t.total_collected)}</span>
          <span className="text-xs text-muted-foreground">/ {formatXOF(t.total_target)}</span>
        </div>
        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
          <div className="h-full gradient-gold-green" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span className="flex items-center gap-1"><Users className="h-3 w-3" />{t.participants_count}/{t.max_participants}</span>
          <span className="flex items-center gap-1"><Coins className="h-3 w-3" />{formatXOF(t.contribution_amount)}/tour</span>
        </div>
      </div>
    </Link>
  );
}

export default function Tontines() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mine, setMine] = useState([]);
  const [joined, setJoined] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    Promise.all([api.get("/tontines/mine"), api.get("/tontines/joined")])
      .then(([m, j]) => { setMine(m.data); setJoined(j.data); })
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="tontines-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Sen Natt</p>
          <h1 className="font-display text-4xl font-bold tracking-tight">Mes tontines</h1>
        </div>
        <Link to="/tontines/new">
          <Button className="btn-gradient rounded-full h-11 px-6" data-testid="create-tontine-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle tontine
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="mine">
        <TabsList className="rounded-full mb-6">
          <TabsTrigger value="mine" className="rounded-full" data-testid="tab-mine">Que j'organise ({mine.length})</TabsTrigger>
          <TabsTrigger value="joined" className="rounded-full" data-testid="tab-joined">Où je participe ({joined.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="mine">
          {mine.length === 0 ? (
            <div className="card-soft p-16 text-center">
              <div className="h-16 w-16 rounded-2xl gradient-gold-green flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">Aucune tontine pour le moment</h3>
              <p className="text-muted-foreground mb-6">Créez votre première tontine et invitez vos proches.</p>
              <Link to="/tontines/new">
                <Button className="btn-gradient rounded-full" data-testid="empty-create-tontine">Créer ma première tontine</Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {mine.map((t) => <TontineCard key={t.id} t={t} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="joined">
          {joined.length === 0 ? (
            <div className="card-soft p-16 text-center text-muted-foreground">
              Vous ne participez pour l'instant à aucune tontine. Demandez à être invité·e via un lien ou un QR.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {joined.map((t) => <TontineCard key={t.id} t={t} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
